/**
 * Upload API Client
 *
 * Provides HTTP client functions for upload operations including:
 * - Creating upload jobs
 * - Uploading files with progress tracking
 * - Completing/cancelling upload jobs
 * - Listing datasets
 * - SSE progress stream URLs
 *
 * @module api/upload
 */

import { apiClient } from './client';
import type {
  CreateUploadJobRequest,
  CreateUploadJobResponse,
  CompleteUploadJobRequest,
  CompleteUploadJobResponse,
  UploadJob,
  Dataset,
  CreateDatasetRequest,
} from '@/features/upload/types/upload.types';

/**
 * Base path for upload API endpoints
 */
const BASE_PATH = '/api/v1/upload';

/**
 * Upload API client methods
 */
export const uploadApi = {
  /**
   * Create a new upload job
   *
   * @param data - Upload job creation parameters
   * @returns Created upload job with optional pre-signed URLs
   *
   * @example
   * ```typescript
   * const { upload_job } = await uploadApi.createUploadJob({
   *   dataset_name: 'my-dataset',
   *   total_files: 5,
   *   validate_schema: true
   * });
   * ```
   */
  createUploadJob: async (
    data: CreateUploadJobRequest
  ): Promise<CreateUploadJobResponse> => {
    const response = await apiClient.post<CreateUploadJobResponse>(
      `${BASE_PATH}/jobs`,
      data
    );
    return response.data;
  },

  /**
   * Upload a single file to an upload job
   *
   * @param uploadJobId - Upload job ID
   * @param file - File to upload
   * @param onProgress - Progress callback (0-100)
   * @returns Upload response with file metadata
   *
   * @example
   * ```typescript
   * const result = await uploadApi.uploadFile(
   *   'job-123',
   *   file,
   *   (progress) => console.log(`${progress}%`)
   * );
   * ```
   */
  uploadFile: async (
    uploadJobId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; file_id: string; message?: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);

    const response = await apiClient.post<{
      success: boolean;
      file_id: string;
      message?: string;
    }>(`${BASE_PATH}/jobs/${uploadJobId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });

    return response.data;
  },

  /**
   * Complete an upload job
   *
   * Marks the job as complete and triggers final processing.
   *
   * @param uploadJobId - Upload job ID
   * @param data - Completion data (status, error message)
   * @returns Updated upload job
   *
   * @example
   * ```typescript
   * await uploadApi.completeUploadJob('job-123', {
   *   status: 'completed'
   * });
   * ```
   */
  completeUploadJob: async (
    uploadJobId: string,
    data: CompleteUploadJobRequest
  ): Promise<CompleteUploadJobResponse> => {
    const response = await apiClient.post<CompleteUploadJobResponse>(
      `${BASE_PATH}/jobs/${uploadJobId}/complete`,
      data
    );
    return response.data;
  },

  /**
   * Cancel an upload job
   *
   * Cancels an in-progress upload job and cleans up uploaded files.
   *
   * @param uploadJobId - Upload job ID
   * @returns Cancelled upload job
   *
   * @example
   * ```typescript
   * await uploadApi.cancelUploadJob('job-123');
   * ```
   */
  cancelUploadJob: async (uploadJobId: string): Promise<{ upload_job: UploadJob }> => {
    const response = await apiClient.post<{ upload_job: UploadJob }>(
      `${BASE_PATH}/jobs/${uploadJobId}/cancel`
    );
    return response.data;
  },

  /**
   * Get upload job details
   *
   * @param uploadJobId - Upload job ID
   * @returns Upload job details
   *
   * @example
   * ```typescript
   * const job = await uploadApi.getUploadJob('job-123');
   * ```
   */
  getUploadJob: async (uploadJobId: string): Promise<UploadJob> => {
    const response = await apiClient.get<UploadJob>(
      `${BASE_PATH}/jobs/${uploadJobId}`
    );
    return response.data;
  },

  /**
   * List all datasets
   *
   * Retrieves a list of all available datasets for selection.
   *
   * @param params - Optional query parameters
   * @returns Array of datasets
   *
   * @example
   * ```typescript
   * const datasets = await uploadApi.listDatasets({
   *   search: 'sales',
   *   limit: 50
   * });
   * ```
   */
  listDatasets: async (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ datasets: Dataset[]; total: number }> => {
    const response = await apiClient.get<{ datasets: Dataset[]; total: number }>(
      '/datasets',
      { params }
    );
    return response.data;
  },

  /**
   * Create a new dataset
   *
   * @param data - Dataset creation parameters
   * @returns Created dataset
   *
   * @example
   * ```typescript
   * const dataset = await uploadApi.createDataset({
   *   name: 'new-dataset',
   *   description: 'Sales data for Q4 2024',
   *   tags: ['sales', 'q4']
   * });
   * ```
   */
  createDataset: async (data: CreateDatasetRequest): Promise<{ dataset: Dataset }> => {
    const response = await apiClient.post<{ dataset: Dataset }>(
      '/datasets',
      data
    );
    return response.data;
  },

  /**
   * Get Server-Sent Events (SSE) stream URL for upload progress
   *
   * Returns the URL to connect an EventSource for real-time progress updates.
   *
   * @param uploadJobId - Upload job ID
   * @returns SSE stream URL
   *
   * @example
   * ```typescript
   * const url = uploadApi.getProgressStreamUrl('job-123');
   * const eventSource = new EventSource(url);
   * ```
   */
  getProgressStreamUrl: (uploadJobId: string): string => {
    // Get base URL from API client config
    const baseURL = apiClient.defaults.baseURL || '';
    const token = localStorage.getItem('auth_token') || '';
    return `${baseURL}${BASE_PATH}/jobs/${uploadJobId}/progress/stream?token=${token}`;
  },

  /**
   * Validate dataset name
   *
   * Checks if a dataset name is valid and available.
   *
   * @param name - Dataset name to validate
   * @returns Validation result
   *
   * @example
   * ```typescript
   * const { valid, message } = await uploadApi.validateDatasetName('my-dataset');
   * if (!valid) {
   *   console.error(message);
   * }
   * ```
   */
  validateDatasetName: async (
    name: string
  ): Promise<{ valid: boolean; message?: string }> => {
    const response = await apiClient.get<{ valid: boolean; message?: string }>(
      '/datasets/validate-name',
      { params: { name } }
    );
    return response.data;
  },

  /**
   * Get dataset by name
   *
   * @param name - Dataset name
   * @returns Dataset details or null if not found
   *
   * @example
   * ```typescript
   * const dataset = await uploadApi.getDatasetByName('sales-data');
   * if (dataset) {
   *   console.log('Dataset exists:', dataset.id);
   * }
   * ```
   */
  getDatasetByName: async (name: string): Promise<Dataset | null> => {
    try {
      const response = await apiClient.get<{ dataset: Dataset }>(
        `/datasets/by-name/${encodeURIComponent(name)}`
      );
      return response.data.dataset;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
};

/**
 * React Query key factory for upload-related queries
 *
 * Provides consistent query keys for caching and invalidation.
 *
 * @example
 * ```typescript
 * const queryKey = uploadKeys.job('job-123');
 * const datasetsKey = uploadKeys.datasets({ search: 'sales' });
 * ```
 */
export const uploadKeys = {
  /**
   * Base key for all upload queries
   */
  all: ['upload'] as const,

  /**
   * Key for upload job queries
   */
  jobs: () => [...uploadKeys.all, 'jobs'] as const,

  /**
   * Key for specific upload job
   */
  job: (id: string) => [...uploadKeys.jobs(), id] as const,

  /**
   * Key for dataset list queries
   */
  datasets: (params?: { search?: string; limit?: number; offset?: number }) =>
    [...uploadKeys.all, 'datasets', params] as const,

  /**
   * Key for specific dataset by name
   */
  datasetByName: (name: string) => [...uploadKeys.all, 'dataset', name] as const,

  /**
   * Key for dataset name validation
   */
  validateName: (name: string) => [...uploadKeys.all, 'validate-name', name] as const,
};
